import { useState, useCallback } from 'react';
import { Card, CardHeader, Button, Badge, Input, Select, LoadingSkeleton, Pagination } from '@nexora/design-system';
import {
  useModuleCtx,
  useRetentionMonitor,
  type RetentionLogStatus,
  LOG_STATUS_LABEL,
  ORIGIN_LABEL,
  logStatusBadgeVariant,
} from './useRetention';
import { formatCurrency, formatDate, formatNumber, formatDocument } from '../../shared/utils/format';

/* ═══════════════════════════════════════════════════════════════════════════
   Monitor de Retenção de Serviços
   (protótipo `erpone-monitor-de-reten-o-de-servi-os`)
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (backend real, DTOs em retention.dto.ts):
   - GET /retention/monitor?status&prestadorCnpj&dateFrom&dateTo&page&limit&sortBy&sortOrder
       → PaginatedRetentionMonitorResponseDto
         { data: RetentionLogDto[], total, page, limit, totalPages,
           totais: { valorBruto, iss, irrf, retidos, naoRetidos, isentos } }

   Colunas do protótipo: Status · NFS-e / Data · Prestador / CNPJ · Valor Bruto ·
   ISS · IRRF · Total Retido (ISS+IRRF, já que o backend não modela PIS/COFINS/CSLL
   como campo próprio do lançamento — ver divergência R3).
   ═══════════════════════════════════════════════════════════════════════════ */

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'ALL', label: 'Todos os status' },
  { value: 'RETIDO', label: 'Retido' },
  { value: 'NAO_RETIDO', label: 'Não Retido' },
  { value: 'ISENTO', label: 'Isento / Não Incide' },
];

export function RetentionMonitorPage(): JSX.Element {
  const ctx = useModuleCtx();
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [cnpjFilter, setCnpjFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const { data, isLoading, error } = useRetentionMonitor(ctx, {
    page,
    limit: 20,
    status: statusFilter === 'ALL' ? undefined : (statusFilter as RetentionLogStatus),
    prestadorCnpj: cnpjFilter || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
  });

  const logs = data?.data ?? [];
  const totais = data?.totais;

  const handleFilter = useCallback(() => {
    setPage(1);
  }, []);

  const handleClear = useCallback(() => {
    setStatusFilter('ALL');
    setCnpjFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  }, []);

  return (
    <div className="module-page">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.75rem', fontWeight: 700, color: '#191c1e' }}>Monitor de Retenção de Serviços</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#43474e', fontSize: '0.875rem' }}>
            Acompanhamento e consolidação de NFS-e sujeitas a retenção tributária.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: '0 0 180px' }}>
            <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </Select>
          </div>
          <div style={{ flex: '1 1 220px' }}>
            <Input label="Prestador (CNPJ)" value={cnpjFilter} onChange={(e) => setCnpjFilter(e.target.value)} placeholder="Buscar fornecedor..." />
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <Input label="Data início" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div style={{ flex: '0 0 180px' }}>
            <Input label="Data fim" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
          <Button variant="secondary" size="sm" onClick={handleFilter}>Filtrar</Button>
          {(statusFilter !== 'ALL' || cnpjFilter || dateFrom || dateTo) && (
            <Button variant="ghost" size="sm" onClick={handleClear}>Limpar</Button>
          )}
        </div>
      </Card>

      {/* Totais */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
        {[
          { label: 'Valor bruto total', value: formatCurrency(totais?.valorBruto), color: '#191c1e' },
          { label: 'ISS retido', value: formatCurrency(totais?.iss), color: '#002045' },
          { label: 'IRRF retido', value: formatCurrency(totais?.irrf), color: '#002045' },
          { label: 'Retidos', value: formatNumber(totais?.retidos, { maximumFractionDigits: 0 }), color: '#2e7d32' },
          { label: 'Não retidos', value: formatNumber(totais?.naoRetidos, { maximumFractionDigits: 0 }), color: '#ed6c02' },
          { label: 'Isentos', value: formatNumber(totais?.isentos, { maximumFractionDigits: 0 }), color: '#74777f' },
        ].map((s) => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e0e3e5', borderRadius: '10px', padding: '1rem' }}>
            <p style={{ margin: 0, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#74777f' }}>{s.label}</p>
            <p style={{ margin: '0.25rem 0 0', fontSize: '1.4rem', fontWeight: 700, color: s.color }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* NFS-e table */}
      <Card variant="outlined" padding="0">
        <CardHeader
          className="px-md pt-md"
          action={
            <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
              {data?.total ?? 0} {(data?.total ?? 0) === 1 ? 'lançamento' : 'lançamentos'}
            </span>
          }
        >
          <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Lançamentos de NFS-e</h3>
        </CardHeader>
        {isLoading && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '3rem', gap: '0.75rem', color: '#43474e' }}>
            <LoadingSkeleton />
            <span style={{ fontSize: '0.875rem' }}>Carregando lançamentos...</span>
          </div>
        )}
        {error && !isLoading && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#ba1a1a' }}>
            <p style={{ fontWeight: 600, margin: '0 0 0.5rem' }}>Erro ao carregar monitor</p>
            <p style={{ fontSize: '0.875rem', margin: 0, color: '#74777f' }}>
              {error instanceof Error ? error.message : 'Erro desconhecido'}
            </p>
            <Button variant="outline" size="sm" onClick={() => window.location.reload()} style={{ marginTop: '1rem' }}>Tentar novamente</Button>
          </div>
        )}
        {!isLoading && !error && logs.length === 0 && (
          <div style={{ padding: '3rem 2rem', textAlign: 'center', color: '#74777f' }}>
            <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>Nenhum lançamento encontrado para os filtros.</p>
            <Button variant="outline" size="sm" onClick={handleClear}>Limpar filtros</Button>
          </div>
        )}
        {!isLoading && !error && logs.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                <tr>
                  {['Status', 'NFS-e / Data', 'Prestador / CNPJ', 'Valor Bruto (R$)', 'ISS', 'IRRF', 'Total Retido', 'Origem'].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: h === 'Valor Bruto (R$)' || h === 'ISS' || h === 'IRRF' || h === 'Total Retido' ? 'right' : 'left',
                        padding: '0.75rem 1rem',
                        background: '#f2f4f6',
                        color: '#43474e',
                        fontWeight: 600,
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        borderBottom: '2px solid #e0e3e5',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} style={{ borderBottom: '1px solid #eceef0' }}>
                    <td style={{ padding: '0.625rem 1rem' }}>
                      <Badge variant={logStatusBadgeVariant(l.status)} size="sm">{LOG_STATUS_LABEL[l.status]}</Badge>
                    </td>
                    <td style={{ padding: '0.625rem 1rem' }}>
                      <div style={{ fontWeight: 600 }}>{l.nfseId ? `NFS-e ${l.nfseId}` : '—'}</div>
                      <div style={{ fontSize: '0.75rem', color: '#74777f' }}>{formatDate(l.dataHora)}</div>
                    </td>
                    <td style={{ padding: '0.625rem 1rem' }}>
                      <div style={{ fontWeight: 500, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {l.prestadorRazaoSocial ?? '—'}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#74777f' }}>{formatDocument(l.prestadorCnpj, 'CNPJ')}</div>
                    </td>
                    <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(l.valorBruto)}
                    </td>
                    <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(l.iss)}
                    </td>
                    <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(l.irrf)}
                    </td>
                    <td style={{ padding: '0.625rem 1rem', textAlign: 'right', fontWeight: 700, color: l.iss + l.irrf > 0 ? '#002045' : '#74777f', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCurrency(l.iss + l.irrf)}
                    </td>
                    <td style={{ padding: '0.625rem 1rem', fontSize: '0.8125rem', color: '#43474e', whiteSpace: 'nowrap' }}>
                      {ORIGIN_LABEL[l.origem]}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!isLoading && !error && (data?.totalPages ?? 1) > 1 && (
          <div style={{ padding: '1rem', borderTop: '1px solid #eceef0' }}>
            <Pagination page={data?.page ?? 1} totalPages={data?.totalPages ?? 1} onChange={(p) => setPage(p)} />
          </div>
        )}
      </Card>

      <style>{`
        .module-page { padding: 1.5rem; }
        .filter-card { margin-bottom: 1rem; }
      `}</style>
    </div>
  );
}